import { MinzeElement, EventListeners, Reactive, Attrs } from 'minze'
import MainModule from '../../modules/main'
import { Locale } from '../../modules/locale'
import { copyTextToClipboard } from '../../modules/utils'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'

Locale.getInstance().registerKeySet('fr', 'lvc-state-confirmation-popup', {
  title: {
    continueSubmission: 'Reprendre la soumission',
    saveAndQuit: 'Sauvegarder et quitter',
    quitWithoutSaving: 'Quitter sans sauvegarder',
    confirmEvent: "Confirmer l'évènement",
    confirmExhibition: "Confirmer l'exposition",
    confirmFestival: 'Confirmer le festival',
    confirmContribution: 'Confirmer la soumission',
    confirmContributor: 'Confirmer le contributeur',
    blockedAccount: 'Compte bloqué',
  },
  textContent: {
    continueSubmission:
      'Une soumission est déjà débutée. Souhaitez-vous la poursuivre ou en commencer une nouvelle?',
    saveAndQuit: 'Votre progression sera sauvegardée et pourra être reprise plus tard.',
    quitWithoutSaving:
      'Êtes-vous sur de vouloir quitter sans sauvegarder? Vous allez perdre votre progression.',
    confirmEvent:
      "Une fois soumis, l'évènement sera révisé par notre équipe avant d'être publié sur lavitrine.com",
    confirmExhibition:
      "Une fois soumise, l'exposition sera révisée par notre équipe avant d'être publiée sur lavitrine.com",
    confirmFestival:
      "Une fois soumis, le festival sera révisé par notre équipe avant d'être publié sur lavitrine.com",
    confirmContribution:
      "Une fois soumis, les liens de contribution seront ajoutés aux événements, représentations, expositions ou festivals listés ici d'ici quelques jours.",
    confirmContributor:
      "Une fois soumis, le contributeur sera révisé par notre équipe avant d'être disponible pour ajout aux événements, représentations, expositions et festivals.",
    blockedAccount:
      "Votre compte a été bloqué. Si vous avez besoin d'aide ou d'informations supplémentaires, vous pouvez nous contacter via le formulaire de signalement.",
  },
  left: {
    continueSubmission: 'Nouvelle soumission',
    saveAndQuit: 'Quitter sans sauvegarder',
    quitWithoutSaving: 'Non, sauvegarder et quitter',
    confirmEvent: 'Modifier la soumission',
    confirmExhibition: 'Modifier la soumission',
    confirmFestival: 'Modifier la soumission',
    confirmContribution: 'Modifier la soumission',
    confirmContributor: 'Modifier la soumission',
    blockedAccount: 'Signaler un problème',
  },
  right: {
    continueSubmission: 'Poursuivre',
    saveAndQuit: 'Sauvegarder et quitter',
    quitWithoutSaving: 'Oui, quitter',
    confirmEvent: 'Soumettre',
    confirmExhibition: 'Soumettre',
    confirmFestival: 'Soumettre',
    confirmContribution: 'Soumettre',
    confirmContributor: 'Soumettre',
    blockedAccount: 'Fermer',
  },
})

Locale.getInstance().registerKeySet('en', 'lvc-state-confirmation-popup', {
  title: {
    continueSubmission: 'Continue submission',
    saveAndQuit: 'Save and quit',
    quitWithoutSaving: 'Quit without saving',
    confirmEvent: 'Confirm event',
    confirmExhibition: 'Confirm exhibition',
    confirmFestival: 'Confirm Festival',
    confirmContribution: 'Confirm submission',
    confirmContributor: 'Confirm contributor',
    blockedAccount: 'Blocked account',
  },
  textContent: {
    continueSubmission: 'A submission is in progress. Do you want to continue or start a new one?',
    saveAndQuit: 'Your progress will be saved and can be resumed later.',
    quitWithoutSaving: 'Are you sure you want to quit without saving? You will lose your progress.',
    confirmEvent:
      'Once submitted, the event will be reviewed by our team before being published on lavitrine.com',
    confirmExhibition:
      'Once submitted, the exhibition will be reviewed by our team before being published on lavitrine.com',
    confirmFestival:
      'Once submitted, the festival will be reviewed by our team before being published on lavitrine.com',
    confirmContribution:
      'Once submitted, the contribution links will be added to the events, representations, exhibitions or festivals listed here within a few days.',
    confirmContributor:
      'Once submitted, the contributor will be reviewed by our team before being available for addition to events, representations, exhibitions and festivals.',
    blockedAccount:
      'Your account has been blocked. If you need help or additional information, you can contact us via the reporting form.',
  },
  left: {
    continueSubmission: 'New submission',
    saveAndQuit: 'Quit without saving',
    quitWithoutSaving: 'No, save and quit',
    confirmEvent: 'Edit submission',
    confirmExhibition: 'Edit submission',
    confirmFestival: 'Edit submission',
    confirmContribution: 'Edit submission',
    confirmContributor: 'Edit submission',
    blockedAccount: 'Report a problem',
  },
  right: {
    continueSubmission: 'Continue',
    saveAndQuit: 'Save and quit',
    quitWithoutSaving: 'Yes, quit',
    confirmEvent: 'Submit',
    confirmExhibition: 'Submit',
    confirmFestival: 'Submit',
    confirmContribution: 'Submit',
    confirmContributor: 'Submit',
    blockedAccount: 'Close',
  },
})

export interface LvcConfirmEventPopup extends MinzeElement {
  onClickBtnLeft?: string
  onClickBtnRight?: string
}

interface PopupPreset {
  title: string
  textContent: string
  leftButtonText: string
  rightButtonText: string
  noCloseButton: boolean
  clipPath?: string
  background?: string
}

const popupPresets: Record<string, PopupPreset> = {
  continueSubmission: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.continueSubmission'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.continueSubmission'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.continueSubmission'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.continueSubmission'),
    noCloseButton: true,
  },
  saveAndQuit: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.saveAndQuit'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.saveAndQuit'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.saveAndQuit'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.saveAndQuit'),
    noCloseButton: false,
  },
  quitWithoutSaving: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.quitWithoutSaving'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.quitWithoutSaving'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.quitWithoutSaving'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.quitWithoutSaving'),
    noCloseButton: false,
  },
  confirmEvent: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.confirmEvent'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.confirmEvent'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.confirmEvent'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.confirmEvent'),
    noCloseButton: true,
  },
  confirmExhibition: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.confirmExhibition'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.confirmExhibition'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.confirmExhibition'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.confirmExhibition'),
    noCloseButton: true,
  },
  confirmFestival: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.confirmFestival'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.confirmFestival'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.confirmFestival'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.confirmFestival'),
    noCloseButton: true,
  },
  confirmContribution: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.confirmContribution'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.confirmContribution'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.confirmContribution'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.confirmContribution'),
    noCloseButton: true,
  },
  confirmContributor: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.confirmContributor'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.confirmContributor'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.confirmContributor'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.confirmContributor'),
    noCloseButton: true,
  },
  blockedAccount: {
    title: Locale.getKey('lvc-state-confirmation-popup.title.blockedAccount'),
    textContent: Locale.getKey('lvc-state-confirmation-popup.textContent.blockedAccount'),
    leftButtonText: Locale.getKey('lvc-state-confirmation-popup.left.blockedAccount'),
    rightButtonText: Locale.getKey('lvc-state-confirmation-popup.right.blockedAccount'),
    noCloseButton: true,
    clipPath: 'url(#artist-slider-big-mask)',
    background: '#ffff',
  },
}
export class LvcStateConfirmationPopup extends MinzeElement {
  reactive: Reactive = []

  attrs: Attrs = [['preset']]

  get popupPreset(): PopupPreset | undefined {
    const preset = this.getAttribute('preset')
    return preset ? popupPresets[preset as keyof typeof popupPresets] : undefined
  }

  onClickLeft(): void {
    Callback.call((this.onClickBtnLeft ?? '') as string)
    this.cast('state-confirmation-popup:left-btn-clicked')
    this.remove()
  }

  onClickRight(): void {
    Callback.call((this.onClickBtnRight ?? '') as string)
    this.cast('state-confirmation-popup:right-btn-clicked')
    this.remove()
  }

  html() {
    return `
    <lvc-simple-modal id="modal" no-close-button="${this.popupPreset?.noCloseButton}" clip-path="true" background="${this.popupPreset?.background}">
      <div class="modal-content">
        <div class="container">

          <div class="title-container">
            <lvc-text font-preset="titleMobileSmall" class="title">
            ${this.popupPreset?.title}
            </lvc-text>

          </div>

          <div class="content-container">
              <lvc-text font-preset="small" class="content" line-height="150%">
              ${this.popupPreset?.textContent}
              </lvc-text>
          </div>
          <div class="bottom-buttons-container">

          <lvc-button enable-hover-box-shadow="true" id="left-button" name="left-button" class="bottom-button" border-radius="999px" height="fit-content" width="100%" padding="14px 24px" font-size="14px" bg="transparent" border="1px solid #1E007D" color="#1E007D">
          ${this.popupPreset?.leftButtonText}
          </lvc-button>
            <lvc-button enable-hover-box-shadow="true" id="right-button" name="right-button" class="bottom-button" border-radius="999px" height="fit-content" width="100%" padding="14px 24px" font-size="14px">
            ${this.popupPreset?.rightButtonText}
            </lvc-button>

          </div>
        </div>

      </div>
    </lvc-simple-modal>
    `
  }

  css = () => `
    :host {
      display: inline;

    }

    .container{
      padding: 20px;
    }

    .modal-content{
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 500px;
    }

    @media screen and (max-width: 730px) {
      .modal-content{
        width: 370px;
      }
    }

    .copy-link-title {
      font-weight: ${Font.weight.medium};
      font-size: 17px;
      line-height: 120%;
      color: #1E007D;
      margin-top: 40px;
      margin-bottom: 8px;
    }

    .title{
      text-transform: uppercase;
    }

    .title-container{
        display:flex;
        align-items: center;
        justify-content: center;
    }

    .content-container{
     margin: 20px;
    }

    .bottom-buttons-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        gap: 16px;
      }

      .bottom-button {
        white-space: nowrap;
        text-overflow: ellipsis;
        cursor: pointer;
        font-size: 14px;
        padding: 14px 24px;
        border-radius: 999px;
        height: fit-content;
        width: fit-content;
        font-size: 14px;
        flex: 1 0 0;
      }


    .newSinceContainer{
      display: flex;
      justify-content: flex-end;
      margin-top: 15px;
      margin-bottom: 15px;
    }


    .copy-link-title.copied {
      color: #FF4600;
    }

    .copy-link-url {
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      display: flex;
      align-items: center;
      color: #1E007D;
    }

    .provider-section {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-evenly;
      margin-top: 40px;
      margin-left: 0;
    }

    .provider-section-logo {
      display: flex;
      align-items: center;
      flex-shrink: 0;

    }

    .providers {
      display: flex;
      flex-direction: row;
      gap: 8px;
    }

    .providers > div {
      display: flex;
      gap: 8px;
    }

    .provider-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 150%;
      text-align: center;
      color: #1E007D;
      cursor: pointer;
    }

    .copy-link-body {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      gap: 8px;

      height: 64px;

      /* Ciel */
      background: #BFF0FF;

      /* Inside auto layout */
      flex: none;
      order: 1;
      align-self: stretch;
      flex-grow: 0;

    }

    .copy-link-button {
      cursor: pointer;
    }

    .copy-link-text-button {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 13px 16px;
      gap: 8px;
      isolation: isolate;
      width: 73px;
      height: 40px;
      border: 1px solid #1E007D;
      border-radius: 999px;
      flex: none;
      order: 1;
      flex-grow: 0;
      font-family: ${Font.family};
      font-style: normal;
      font-weight: ${Font.weight.medium};
      font-size: 12px;
      line-height: 120%;
      font-feature-settings: 'tnum' on, 'lnum' on;
      color: #1E007D;
    }

    .provider-label {
      margin-top: 8px;
    }

    .provider-icon {
      width: 80px;
      height: 80px;
      border: 1px solid #1E007D;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .provider-icon {
      width: 80px;
      height: 80px;
      border: 1px solid #1E007D;
      border-radius: 999px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @media screen and (max-width: 730px) {
      .copy-link-desktop {
        display: none;
      }
    }
    @media screen and (min-width: 730px) {
      .copy-link-mobile {
        display: none;
      }
    }

  `

  eventListeners: EventListeners = [
    ['[name="left-button"]', 'click', this.onClickLeft.bind(this)],
    ['[name="right-button"]', 'click', this.onClickRight.bind(this)],
  ]
}
